var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import 'codemirror/mode/javascript/javascript';
import 'codemirror/lib/codemirror.css';
import './style.css';
import { createCardDOM, createMaskDOM, createTemplDOM, EventChannel } from '@syllepsis/adapter';
import CodeMirror from 'codemirror';
import debounce from 'lodash.debounce';
import { exitCode } from 'prosemirror-commands';
import { Selection, TextSelection } from 'prosemirror-state';
import { config } from './config';
import { DEFAULT_LANGUAGE, fixCodeString, removeEmptyCodeBlock } from './utils';
// codeblock is special formats witch contains text content, behave like block
// and we need to control all of its behavior, so we treat it as built-in formats to support better user experience
// to achieve it, we use `NodeView` feature ProseMirror supply
// https://prosemirror.net/docs/ref/#view.NodeView
var CodeBlockView = /** @class */ (function () {
    function CodeBlockView(editor, node, view, getPos) {
        var _this = this;
        this.boundEvent = function () {
            // Track whether changes are have been made but not yet propagated
            _this.cm.on('beforeChange', function () { return (_this.incomingChanges = true); });
            // Propagate updates from the code editor to ProseMirror
            _this.cm.on('cursorActivity', function () {
                if (!_this.updating && !_this.incomingChanges)
                    _this.forwardSelection();
            });
            _this.cm.on('changes', function () {
                if (!_this.updating) {
                    _this.valueChanged();
                    _this.forwardSelection();
                }
                _this.incomingChanges = false;
            });
            _this.cm.on('focus', _this.forwardSelection);
            _this.editor.on(EventChannel.LocalEvent.ON_CHANGE, _this.checkEditable);
        };
        this.checkEditable = debounce(function () {
            if (_this.editor.editable && _this.cm.isReadOnly()) {
                _this.cm.setOption('readOnly', false);
            }
            else if (!_this.editor.editable && !_this.cm.isReadOnly()) {
                _this.cm.setOption('readOnly', 'nocursor');
            }
        }, 300, { leading: true });
        this.forwardSelection = function () {
            if (!_this.cm.hasFocus())
                return;
            var state = _this.view.state;
            var selection = _this.asProseMirrorSelection(state.doc);
            if (!selection.eq(state.selection)) {
                _this.view.dispatch(state.tr.setSelection(selection));
            }
        };
        // Store for later
        this.node = node;
        this.view = view;
        this.getPos = getPos;
        this.editor = editor;
        this.incomingChanges = false;
        // The editor's outer node is our DOM representation
        this.dom = createCardDOM();
        var maskDOM = createMaskDOM();
        this.templ = createTemplDOM();
        this.dom.appendChild(this.templ);
        this.dom.appendChild(maskDOM);
        // Create a CodeMirror instance
        this.cm = CodeMirror(maskDOM, __assign({ value: this.node.textContent, tabSize: 2, mode: node.attrs.language || DEFAULT_LANGUAGE, extraKeys: this.codeMirrorKeymap(editor) }, config));
        this.boundEvent();
        this.updateTemplValue();
        // CodeMirror needs to be in the DOM to properly initialize, so schedule it to update itself
        setTimeout(function () { return _this.cm.refresh(); }, 20);
        // This flag is used to avoid an update loop between the outer and inner editor
        this.updating = false;
        this.dom.setAttribute('language', node.attrs.language);
        this.dom.setAttribute('code_block', 'true');
    }
    CodeBlockView.prototype.asProseMirrorSelection = function (doc) {
        var offset = this.getPos() + 1;
        var anchor = this.cm.indexFromPos(this.cm.getCursor('from')) + offset;
        var head = this.cm.indexFromPos(this.cm.getCursor('to')) + offset;
        return TextSelection.create(doc, anchor, head);
    };
    CodeBlockView.prototype.setSelection = function (anchor, head) {
        this.updating = true;
        this.cm.setSelection(this.cm.posFromIndex(anchor), this.cm.posFromIndex(head));
        if (!this.cm.hasFocus())
            this.cm.focus();
        this.updating = false;
    };
    CodeBlockView.prototype.valueChanged = function () {
        var change = computeChange(this.node.textContent, this.cm.getValue());
        if (change) {
            var start = this.getPos() + 1;
            var tr = this.view.state.tr.replaceWith(start + change.from, start + change.to, change.text ? this.view.state.schema.text(change.text) : null);
            this.updateTemplValue();
            this.view.dispatch(tr);
        }
    };
    CodeBlockView.prototype.codeMirrorKeymap = function (editor) {
        var _a;
        var _this = this;
        var view = editor.view;
        var cm = function () { return _this.cm; };
        var mod = /Mac/.test(navigator.platform) ? 'Cmd' : 'Ctrl';
        var undo = function () {
            editor.undo();
            editor.focus();
        };
        var redo = function () {
            editor.redo();
            editor.focus();
        };
        return CodeMirror.normalizeKeyMap((_a = {
                Up: function () { return _this.maybeEscape('line', -1); },
                Left: function () { return _this.maybeEscape('char', -1); },
                Down: function () { return _this.maybeEscape('line', 1); },
                Right: function () { return _this.maybeEscape('char', 1); }
            },
            _a[mod + "-Z"] = undo,
            _a["Shift-" + mod + "-Z"] = redo,
            _a[mod + "-Y"] = redo,
            _a[mod + "-Enter"] = function () {
                if (exitCode(view.state, view.dispatch))
                    view.focus();
            },
            _a.Enter = function () {
                var _cm = cm();
                var lineCount = _cm.lineCount();
                var cursorLine = _cm.getCursor().line;
                if (lineCount > 1 && cursorLine === lineCount - 1) {
                    var curLine = _cm.getLine(cursorLine);
                    if (curLine === '') {
                        var prevLine = _cm.getLine(cursorLine - 1);
                        if (prevLine === '') {
                            _cm.execCommand('delCharBefore');
                            _cm.execCommand('delCharBefore');
                            if (_cm.getValue().length === 0) {
                                var pos = view.state.selection.$from.before();
                                var end = view.state.selection.$from.after();
                                view.dispatch(view.state.tr.delete(pos, end));
                                view.focus();
                            }
                            else if (exitCode(view.state, view.dispatch)) {
                                view.focus();
                            }
                            return;
                        }
                    }
                }
                _cm.execCommand('newlineAndIndent');
            },
            _a.Backspace = function () {
                var _cm = cm();
                if (removeEmptyCodeBlock(view, _cm)) {
                    view.focus();
                }
                else {
                    _cm.execCommand('delCharBefore');
                }
            },
            _a));
    };
    CodeBlockView.prototype.maybeEscape = function (unit, dir) {
        var pos = this.cm.getCursor();
        if (this.cm.somethingSelected() ||
            pos.line !== (dir < 0 ? this.cm.firstLine() : this.cm.lastLine()) ||
            (unit === 'char' && pos.ch !== (dir < 0 ? 0 : this.cm.getLine(pos.line).length))) {
            return CodeMirror.Pass;
        }
        this.view.focus();
        var targetPos = this.getPos() + (dir < 0 ? 0 : this.node.nodeSize);
        var selection = Selection.near(this.view.state.doc.resolve(targetPos), dir);
        this.view.dispatch(this.view.state.tr.setSelection(selection).scrollIntoView());
        this.view.focus();
    };
    CodeBlockView.prototype.updateTemplValue = function () {
        this.templ.innerHTML = "<pre language=\"" + this.node.attrs.language + "\" code_block=\"true\"><code>" + fixCodeString(this.cm.getValue()) + "</code></pre>";
    };
    CodeBlockView.prototype.update = function (node) {
        if (node.type !== this.node.type)
            return false;
        this.node = node;
        var change = computeChange(this.cm.getValue(), node.textContent);
        if (change) {
            this.updating = true;
            this.cm.replaceRange(change.text, this.cm.posFromIndex(change.from), this.cm.posFromIndex(change.to));
            this.updating = true;
        }
        return true;
    };
    // Can be used to override the way the node's selected status (as a node selection) is displayed.
    CodeBlockView.prototype.selectNode = function () {
        this.cm.focus();
    };
    CodeBlockView.prototype.stopEvent = function () {
        return true;
    };
    CodeBlockView.prototype.destroy = function () {
        this.editor.off(EventChannel.LocalEvent.ON_CHANGE, this.checkEditable);
    };
    return CodeBlockView;
}());
function computeChange(oldVal, newVal) {
    if (oldVal === newVal)
        return null;
    var start = 0;
    var oldEnd = oldVal.length;
    var newEnd = newVal.length;
    while (start < oldEnd && oldVal.charCodeAt(start) === newVal.charCodeAt(start)) {
        ++start;
    }
    while (oldEnd > start && newEnd > start && oldVal.charCodeAt(oldEnd - 1) === newVal.charCodeAt(newEnd - 1)) {
        oldEnd--;
        newEnd--;
    }
    return {
        from: start,
        to: oldEnd,
        text: newVal.slice(start, newEnd),
    };
}
export { CodeBlockView };
//# sourceMappingURL=view.js.map